<template>
  <section :style="{ backgroundColor: bgColor }">
    <div class="py-10 md:py-20">
      <div class="px-4 mx-auto max-w-6xl md:px-6 xl:px-0">
        <!-- Heading -->
        <h2 class="text-2xl text-center md:leading-10 xl:text-42 sm:text-3xl md:text-4xl font-ww-bold" v-html="mainTitle"></h2>

       <div class="mt-5 space-y-10 md:space-y-20 md:mt-14 lg:mt-28 xl:space-y-32">
         <!-- Item 1 -->
         <div class="grid grid-cols-1 gap-8 items-center md:grid-cols-2 xl:gap-x-32">
          <div class="order-1">
            <nuxt-img 
              :src="item1ImageSrc" 
              :alt="item1ImageAlt"
              class="object-cover w-full" 
              width="543" 
              height="469"
              format="webp"
              loading="lazy"
              sizes="xs:300px sm:393px md:720px lg:464px xl:536px 2xl:543px"
            />
          </div>
          <div class="order-2">
            <h3 class="font-bold text-center text-22 xl:text-3xl md:text-2xl font-ww-bold md:text-left">{{ item1Title }}</h3>
            <div class="mt-3 text-lg italic font-ww-regular xl:text-22">
              "{{ item1Subtitle }}"
            </div>
            <div class="mt-5 lg:mt-10 xl:text-xl font-ww-regular" v-html="item1Description"></div>
          </div>
        </div>

        <!-- Item 2 -->
        <div class="grid grid-cols-1 gap-8 items-center md:grid-cols-2 xl:gap-x-28">
          <div class="order-2 md:order-1">
            <h3 class="font-bold text-center text-22 xl:text-3xl md:text-2xl font-ww-bold md:text-left">{{ item2Title }}</h3>
            <div class="mt-2 text-lg italic font-ww-regular xl:text-22">
              "{{ item2Subtitle }}"
            </div>
            <div class="mt-5 lg:mt-10 xl:text-xl font-ww-regular" v-html="item2Description"></div>
          </div>
          <div class="order-1 md:order-2 xl:pl-2">
            <nuxt-img 
              :src="item2ImageSrc" 
              :alt="item2ImageAlt"
              class="object-cover w-full" 
              width="562" 
              height="492"
              format="webp"
              loading="lazy"
             sizes="xs:300px sm:393px md:720px lg:464px xl:536px 2xl:543px"
            />
          </div>
        </div>

        <!-- Item 3 -->
        <div class="grid grid-cols-1 gap-8 items-center xl:items-start md:grid-cols-2 xl:gap-x-0">
          <div class="order-1">
            <nuxt-img 
              :src="item3ImageSrc" 
              :alt="item3ImageAlt"
              class="object-cover w-full" 
              width="608" 
              height="490"
              format="webp"
              loading="lazy"
              sizes="xs:300px sm:393px md:720px lg:464px xl:536px 2xl:543px"
            />
          </div>
          <div class="order-2 xl:pl-16">
            <h3 class="font-bold text-center text-22 xl:text-3xl md:text-2xl font-ww-bold md:text-left">{{ item3Title }}</h3>
            <div class="mt-2 text-lg italic font-ww-regular xl:text-22">
              "{{ item3Subtitle }}"
            </div>
            <div class="mt-5 lg:mt-10 xl:text-xl font-ww-regular" v-html="item3Description"></div>
          </div>
        </div>
       </div>

        <!-- References -->
        <div class="mt-10 max-w-3xl">
          <div class="text-sm font-ww-regular">
            <span class="font-medium">{{ referencesTitle }}:</span>
            <ul class="pl-4 list-disc">
              <li v-for="(reference, index) in references" :key="index">
                {{ typeof reference === 'string' ? reference : reference.reference }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, toRefs } from 'vue'

const props = defineProps({
  bgColor: {
    type: String,
    default: "#F9F5ED"
  },
  mainTitle: {
    type: String,
    default: "Science &amp; Quality You Can Trust"
  },
  item1ImageSrc: {
    type: String,
    default: "assets/images/trust-1.png"
  },
  item1ImageAlt: {
    type: String,
    default: "Signature Low Acid Coffee beans"
  },
  item1Title: {
    type: String,
    default: "Signature Low Acid Coffee"
  },
  item1Subtitle: {
    type: String,
    default: "The cleanest, smoothest coffee—refined beyond industry standards."
  },
  item1Description: {
    type: String,
    default: "Every cup of Cognition starts with our single-origin, shade-grown Arabica beans, cultivated high in the mountains where slower growth creates a smoother, richer flavor. Coffee is one of the most chemically treated crops in the world, which is why we go further. Each batch is processed using our TrustPure™ method and third-party tested for 450+ toxins—including pesticides, mycotoxins, heavy metals, and mold. The result: a clean, low-acid coffee that's as gentle on your stomach as it is satisfying to your taste buds."
  },
  item2ImageSrc: {
    type: String,
    default: "assets/images/trust-2.png"
  },
  item2ImageAlt: {
    type: String,
    default: "Lion's Mane Mushroom Extract"
  },
  item2Title: {
    type: String,
    default: "Lion's Mane Mushroom Extract (8:1)"
  },
  item2Subtitle: {
    type: String,
    default: "The mushroom studied for its role in supporting memory and focus."
  },
  item2Description: {
    type: String,
    default: "We use only the fruiting bodies of organic Lion's Mane (Hericium erinaceus), never mycelium, to deliver the highest concentration of active compounds. Research has shown that Lion's Mane contains hericenones and erinacines, bioactive molecules studied for their ability to stimulate Nerve Growth Factor (NGF) and support healthy cognitive function. By sourcing through Nammex, the global leader in mushroom extracts, we ensure every serving delivers consistent potency, purity, and efficacy."
  },
  item3ImageSrc: {
    type: String,
    default: "assets/images/trust-3.png"
  },
  item3ImageAlt: {
    type: String,
    default: "Chaga Mushroom Extract"
  },
  item3Title: {
    type: String,
    default: "Chaga Mushroom Extract (8:1)"
  },
  item3Subtitle: {
    type: String,
    default: "Nature's antioxidant powerhouse—supporting resilience from the inside out."
  },
  item3Description: {
    type: String,
    default: "Revered in traditional wellness practices across Northern Europe and Asia, Chaga (Inonotus obliquus) is prized for its antioxidant and immune-modulating compounds, including beta-glucans, polyphenols, and melanin. Modern research has highlighted Chaga's potential to support balanced immune responses and protect against oxidative stress. Like our Lion's Mane, our Chaga is sourced from Nammex and subjected to rigorous testing to guarantee safety and quality."
  },
  referencesTitle: {
    type: String,
    default: "References"
  },
  references: {
    type: Array,
    default: () => [
      "Harvard T.H. Chan School of Public Health – Coffee and Health (Caffeine, Antioxidants, and Disease Risk)",
      "NIH National Library of Medicine – Research on Lion's Mane & NGF stimulation (Hericenones, Erinacines)",
      "NIH National Library of Medicine – Research on Chaga's antioxidant and beta-glucan profile"
    ]
  }
})

// expose props to template as refs (improves IDE type inference)
const {
  bgColor,
  mainTitle,
  item1ImageSrc,
  item1ImageAlt,
  item1Title,
  item1Subtitle,
  item1Description,
  item2ImageSrc,
  item2ImageAlt,
  item2Title,
  item2Subtitle,
  item2Description,
  item3ImageSrc,
  item3ImageAlt,
  item3Title,
  item3Subtitle,
  item3Description,
  referencesTitle,
  references
} = toRefs(props)

defineBuilderComponent({
  friendlyName: "Text Image Split Ingredients",
  inputs: [
    {
      name: "bgColor",
      type: "color",
      defaultValue: "#F9F5ED"
    },
    {
      name: "mainTitle",
      type: "longText",
      defaultValue: "Science &amp; Quality You Can Trust"
    },
    {
      name: "item1ImageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "assets/images/trust-1.png"
    },
    {
      name: "item1ImageAlt",
      type: "text",
      defaultValue: "Signature Low Acid Coffee beans"
    },
    {
      name: "item1Title",
      type: "text",
      defaultValue: "Signature Low Acid Coffee"
    },
    {
      name: "item1Subtitle",
      type: "text",
      defaultValue: "The cleanest, smoothest coffee—refined beyond industry standards."
    },
    {
      name: "item1Description",
      type: "longText",
      defaultValue: "Every cup of Cognition starts with our single-origin, shade-grown Arabica beans, cultivated high in the mountains where slower growth creates a smoother, richer flavor. Coffee is one of the most chemically treated crops in the world, which is why we go further. Each batch is processed using our TrustPure™ method and third-party tested for 450+ toxins—including pesticides, mycotoxins, heavy metals, and mold. The result: a clean, low-acid coffee that's as gentle on your stomach as it is satisfying to your taste buds."
    },
    {
      name: "item2ImageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "assets/images/trust-2.png"
    },
    {
      name: "item2ImageAlt",
      type: "text",
      defaultValue: "Lion's Mane Mushroom Extract"
    },
    {
      name: "item2Title",
      type: "text",
      defaultValue: "Lion's Mane Mushroom Extract (8:1)"
    },
    {
      name: "item2Subtitle",
      type: "text",
      defaultValue: "The mushroom studied for its role in supporting memory and focus."
    },
    {
      name: "item2Description",
      type: "longText",
      defaultValue: "We use only the fruiting bodies of organic Lion's Mane (Hericium erinaceus), never mycelium, to deliver the highest concentration of active compounds. Research has shown that Lion's Mane contains hericenones and erinacines, bioactive molecules studied for their ability to stimulate Nerve Growth Factor (NGF) and support healthy cognitive function. By sourcing through Nammex, the global leader in mushroom extracts, we ensure every serving delivers consistent potency, purity, and efficacy."
    },
    {
      name: "item3ImageSrc",
      type: "file",
      allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
      defaultValue: "assets/images/trust-3.png"
    },
    {
      name: "item3ImageAlt",
      type: "text",
      defaultValue: "Chaga Mushroom Extract"
    },
    {
      name: "item3Title",
      type: "text",
      defaultValue: "Chaga Mushroom Extract (8:1)"
    },
    {
      name: "item3Subtitle",
      type: "text",
      defaultValue: "Nature's antioxidant powerhouse—supporting resilience from the inside out."
    },
    {
      name: "item3Description",
      type: "longText",
      defaultValue: "Revered in traditional wellness practices across Northern Europe and Asia, Chaga (Inonotus obliquus) is prized for its antioxidant and immune-modulating compounds, including beta-glucans, polyphenols, and melanin. Modern research has highlighted Chaga's potential to support balanced immune responses and protect against oxidative stress. Like our Lion's Mane, our Chaga is sourced from Nammex and subjected to rigorous testing to guarantee safety and quality."
    },
    {
      name: "referencesTitle",
      type: "text",
      defaultValue: "References"
    },
    {
      name: "references",
      type: "list",
      subFields: [
        {
          name: "reference",
          type: "text",
          required: true
        }
      ],
      defaultValue: [
        { reference: "Harvard T.H. Chan School of Public Health – Coffee and Health (Caffeine, Antioxidants, and Disease Risk)" },
        { reference: "NIH National Library of Medicine – Research on Lion's Mane & NGF stimulation (Hericenones, Erinacines)" },
        { reference: "NIH National Library of Medicine – Research on Chaga's antioxidant and beta-glucan profile" }
      ]
    }
  ]
})
</script>